<template>
  <v-container fluid>
    <div>
      <v-row justify="center" class="mt-n8">
        <v-icon class="mx-2" size="35px">assured_workload</v-icon>
        <span style="font-size: 34px; color: #616161">Bancos</span>
      </v-row>
      <v-row justify="center" class="mt-n2">
        <span style="color: #616161">
          Controla tus movimientos de dinero con tus cuentas de banco, efectivo y tarjetas
          de crédito.
        </span>
      </v-row>

      <v-row justify="center" class="">
        <v-col cols="12" md="12">
          <v-row justify="center">
            <search-header :show_department="false" :show_location="false" :show_accounts="false" :show_weeks="false"
              :show_refresh="false" @dataFilters="getData($event)" />
          </v-row>
        </v-col>
      </v-row>

      <!-- CARDS DE RESUMEN POR MONEDA -->
      <v-row class="mt-n7">
        <v-col cols="12" md="4">
          <v-row v-if="loadingSummary">
            <v-col cols="12">
              <v-skeleton-loader type="card"></v-skeleton-loader>
            </v-col>
          </v-row>
          <v-row v-else>
            <!-- Loop through each currency -->
            <v-col cols="12" v-for="currency in summaryCurrencies" :key="currency.currency_code">
              <v-card class="pa-4 mb-2" elevation="2">
                <v-row align="center" class="mb-2">
                  <v-col cols="auto">
                    <v-avatar :color="getCurrencyColor(currency.currency_id)" size="40" class="white--text">
                      <span class="font-weight-bold">{{ currency.currency_code }}</span>
                    </v-avatar>
                  </v-col>
                  <v-col>
                    <div class="text-overline grey--text">{{ getCurrencyName(currency.currency_id) }}</div>
                  </v-col>
                </v-row>

                <v-divider class="mb-3"></v-divider>

                <v-row>
                  <v-col cols="6" class="pr-2">
                    <div class="text-caption grey--text mb-1">Saldo Disponible</div>
                    <div class="text-h6 teal--text font-weight-bold">
                      {{ currencyFormat(currency.total_bank_cash) }}
                    </div>
                  </v-col>
                  <v-col cols="6" class="pl-2">
                    <div class="text-caption grey--text mb-1">Deuda Crédito</div>
                    <div class="text-h6 red--text font-weight-bold">
                      {{ currencyFormat(currency.total_credit_debt) }}
                    </div>
                  </v-col>
                </v-row>

                <v-card-text class="text-caption pa-0 mt-2">
                  Actualizado {{ getDate(currency.last_updated) }}
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Empty state -->
            <v-col cols="12" v-if="summaryCurrencies.length === 0">
              <v-alert type="info" text dense>
                No hay información de saldos disponible
              </v-alert>
            </v-col>
          </v-row>
        </v-col>

        <!-- GRÁFICO DE INGRESOS Y EGRESOS -->
        <v-col cols="12" md="8">
          <v-card :loading="loadingChart">
            <v-card-title class="text-h6">Ingresos y egresos</v-card-title>
            <v-col cols="12">
              <apexchart v-if="!loadingChart && chartData.length > 0" height="170%" type="bar" :options="chartOptions"
                :series="series"></apexchart>
              <v-row v-else-if="!loadingChart && chartData.length === 0" justify="center" class="pa-8">
                <v-col cols="12" class="text-center">
                  <v-icon size="64" color="grey lighten-1">show_chart</v-icon>
                  <p class="grey--text mt-4">
                    No hay datos de transacciones para mostrar
                  </p>
                </v-col>
              </v-row>
            </v-col>
          </v-card>
        </v-col>
      </v-row>

      <!-- TABLA DE CUENTAS -->
      <v-row justify="center" class="mt-n8">
        <v-col cols="12">
          <v-data-table :headers="headers" :items="accounts_banks" :loading="loading" hide-default-footer
            class="elevation-1 mt-6">
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title class="mx-2">Cuentas de bancos</v-toolbar-title>
                <v-divider class="mx-7" inset vertical></v-divider>
                <v-spacer></v-spacer>
                <v-text-field v-model="search" class="text-xs-center" append-icon="search" label="Buscar" single-line
                  hide-details @input="filterAccounts">
                </v-text-field>
                <v-spacer></v-spacer>
                <v-btn @click="(formData = {}), (dialog = true)" color="primary" outlined class="mb-2">
                  <v-icon small>mdi-plus</v-icon>
                  Nueva Cuenta
                </v-btn>
              </v-toolbar>
            </template>

            <template v-slot:[`item.account_name`]="{ item }">
              <v-card class="pa-2 mb-1 elevation-0">
                <v-row no-gutters align="center">
                  <v-col cols="3" class="d-flex justify-center align-center">
                    <v-avatar size="60">
                      <v-img contain :src="getIconBank(item.account_bank_id)" alt="Bank Icon"></v-img>
                    </v-avatar>
                  </v-col>
                  <v-col cols="9">
                    <v-row>
                      <v-col cols="12">
                        <span class="font-weight-bold" style="color: #3a3a3a; font-size: 16px">
                          {{ item.account_name }}
                        </span>
                        <span class="ml-1" style="color: #333; font-size: 14px">
                          ({{ item.currency_code }})
                        </span>
                      </v-col>
                    </v-row>
                    <v-row class="mt-n5">
                      <v-col cols="12">
                        <span class="text--secondary" style="color: #6c757d; font-size: 14px">
                          {{ item.bank_description_long }}
                        </span>
                      </v-col>
                    </v-row>
                    <v-row class="mt-n5">
                      <v-col cols="12">
                        <span class="text--secondary font-weight-bold">
                          Cta. {{ item.account_check_digit }}
                        </span>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card>
            </template>

            <template v-slot:[`item.account_type_id`]="{ item }">
              {{ item.account_type_description }}
            </template>

            <template v-slot:[`item.chart_of_account_id`]="{ item }">
              <v-col cols="12">
                <v-row>
                  <span class="font-weight-bold">{{ item.chart_of_account_code }}</span>
                </v-row>
                <v-row>
                  <span>{{ item.chart_of_account_name }}</span>
                </v-row>
              </v-col>
            </template>

            <template v-slot:[`item.saldo_contable`]="{ item }">
              <span :class="getSaldoColor(item.saldo_contable)" class="font-weight-bold">
                {{ currencyFormat(item.saldo_contable) }} {{ item.currency_code }}
              </span>
            </template>

            <template v-slot:[`item.last_updated`]="{ item }">
              <span class="text-caption">
                {{ getDate(item.last_updated) }}
              </span>
            </template>

            <template v-slot:bottom> </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" color="blue" class="mx-1" @click="viewAccountBank(item)">
                    visibility
                  </v-icon>
                </template>
                <span>Ver</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" color="orange" class="mx-1" @click="editAccount(item)">
                    edit
                  </v-icon>
                </template>
                <span>Editar</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" color="green" class="mx-1" @click="updateStatusAccount(item)">
                    check_circle
                  </v-icon>
                </template>
                <span>Conciliar</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </div>

    <!-- DIALOG PARA NUEVA CUENTA -->
    <v-dialog v-model="dialog" max-width="800px">
      <Account :business_id="lastFilters.business" :formData="formData" @close="dialog = false" @refresh="
        dialog = false;
      refreshData();
      " :last-filters="lastFilters" />
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";
import Account from "@/components/Banks/Account.vue";

export default {
  data: () => ({
    loading: false,
    loadingSummary: false,
    loadingChart: false,
    dialog: false,
    search: "",
    formData: {
      representative_email: "",
    },
    accounts_banks: [],
    filteredAccounts: [],
    summaryCurrencies: [], // Array of currency balances from backend
    chartData: [],
    headers: [
      { text: "Nombre de cuenta", value: "account_name", width: "35%" },
      { text: "Tipo de cuenta", value: "account_type_id", width: "15%" },
      { text: "Saldo", value: "saldo_contable", width: "15%" },
      { text: "Cuenta contable", value: "chart_of_account_id", width: "20%" },
      { text: "Última actualización", value: "last_updated", width: "10%" },
      { text: "Acciones", value: "actions", sortable: false, width: "5%" },
    ],
    lastFilters: {},
  }),

  components: {
    Account,
  },

  computed: {
    series() {
      if (!this.chartData || this.chartData.length === 0) {
        return [
          { name: "Ingresos", data: [] },
          { name: "Egresos", data: [] },
        ];
      }

      return [
        {
          name: "Ingresos",
          data: this.chartData.map((item) => item.ingresos || 0),
        },
        {
          name: "Egresos",
          data: this.chartData.map((item) => item.egresos || 0),
        },
      ];
    },

    chartOptions() {
      return {
        chart: {
          type: "line",
          height: 350,
          toolbar: {
            show: false,
          },
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        xaxis: {
          categories: this.chartData.map((item) => item.month_name || ""),
        },
        yaxis: {
          labels: {
            formatter: function (value) {
              return `RD$${(value / 1000).toFixed(0)}K`;
            },
          },
        },
        colors: ["#4CAF50", "#F44336"],
        dataLabels: {
          enabled: false,
        },
        legend: {
          position: "top",
        },
        grid: {
          borderColor: "#e7e7e7",
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.5,
          },
        },
        markers: {
          size: 6,
        },
      };
    },
  },

  created() {
    moment.locale("es");
  },

  methods: {
    async getData(data) {
      this.lastFilters = data;
      this.formData.business = data.business;

      // Ejecutar todas las llamadas en paralelo
      await Promise.all([
        this.getAccountsData(data.business),
        this.getSummaryData(data.business),
        this.getChartData(data.business),
      ]);
    },

    async getAccountsData(businessId) {
      this.loading = true;
      try {
        const request = await this.api_Post("humanResource/businessAccountById", {
          id: businessId,
        });

        if (request.status === 200) {
          const result = await request.json();
          this.accounts_banks = result;
          this.filteredAccounts = [...result];
        }
      } catch (error) {
        console.error("Error loading accounts:", error);
        this.$toast.error("Error al cargar las cuentas");
      } finally {
        this.loading = false;
      }
    },

    async getSummaryData(businessId) {
      this.loadingSummary = true;
      try {
        const request = await this.api_Post("humanResource/businessAccountSummary", {
          id: businessId,
        });

        if (request.status === 200) {
          const result = await request.json();
          // Backend now returns an array of currencies
          this.summaryCurrencies = Array.isArray(result) ? result : [];
        }
      } catch (error) {
        console.error("Error loading summary:", error);
        this.summaryCurrencies = [];
      } finally {
        this.loadingSummary = false;
      }
    },

    async getChartData(businessId) {
      this.loadingChart = true;
      try {
        const request = await this.api_Post(
          "humanResource/businessAccountTransactionHistory",
          {
            id: businessId,
            months: 6,
          }
        );

        if (request.status === 200) {
          const result = await request.json();
          this.chartData = result;
        }
      } catch (error) {
        console.error("Error loading chart data:", error);
        this.chartData = [];
      } finally {
        this.loadingChart = false;
      }
    },

    async refreshData() {
      if (this.lastFilters.business) {
        await this.getData(this.lastFilters);
      }
    },

    filterAccounts() {
      if (!this.search) {
        this.accounts_banks = [...this.filteredAccounts];
        return;
      }

      const searchLower = this.search.toLowerCase();
      this.accounts_banks = this.filteredAccounts.filter(
        (account) =>
          account.account_name.toLowerCase().includes(searchLower) ||
          account.bank_description_long.toLowerCase().includes(searchLower) ||
          account.account_check_digit.toString().includes(searchLower)
      );
    },

    editAccount(item) {
      this.formData = { ...item };
      this.dialog = true;
    },

    viewAccountBank(item) {
      this.$router.push({
        name: "ViewAccountBank",
        params: { id: item.account_id },
      });
    },

    async updateStatusAccount(item) {
      // Implementar lógica de conciliación
      this.$toast.info("Función de conciliación en desarrollo");
    },

    getSaldoColor(saldo) {
      const amount = parseFloat(saldo) || 0;
      if (amount > 0) return "green--text";
      if (amount < 0) return "red--text";
      return "grey--text";
    },

    getDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },

    getCurrencyName(currencyId) {
      const names = {
        1: "Pesos Dominicanos",
        2: "Gourdes Haitianos",
        3: "Dólares Estadounidenses"
      };
      return names[currencyId] || "Moneda";
    },

    getCurrencyColor(currencyId) {
      const colors = {
        1: "blue",      // DOP - Pesos
        2: "orange",    // HTG - Gourdes
        3: "green"      // USD - Dólares
      };
      return colors[currencyId] || "grey";
    },

    currencyFormat(amount) {
      const number = parseFloat(amount) || 0;
      return new Intl.NumberFormat("es-DO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(number);
    },
  },
};
</script>

<style scoped>
.text--success {
  color: #4caf50 !important;
}

.text--error {
  color: #f44336 !important;
}

.v-divider {
  margin: 0 !important;
}

.mx-2 {
  margin-left: 8px !important;
  margin-right: 8px !important;
}

.elevation-0 {
  box-shadow: none !important;
}

.green--text {
  color: #4caf50 !important;
}

.red--text {
  color: #f44336 !important;
}

.grey--text {
  color: #757575 !important;
}
</style>
